<template>
    <div class="switch-container">
        <div 
            ref="switchContainer" 
            class="switch" 
            @click="handleClick" 
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove" 
            @touchend="handleTouchEnd"
            :style="{ '--switch-value': internalValue }"
        >
            <div class="switch-knob" :style="{ backgroundColor: knobColor }">
                <div class="knob-inner"></div>
            </div>
        </div>
        <div class="label-container">
            <span :style="{ color: labelColor }">{{ stateLabel }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

const props = defineProps<{
    value: 0 | 1 | 2;
}>();

const emit = defineEmits<{
    (e: 'change', newValue: 0 | 1 | 2): void;
}>();

const internalValue = ref(props.value);
const switchContainer = ref<HTMLElement | null>(null);

let startTouchX = 0;
let endTouchX = 0;

const stateLabel = computed(() => {
    return ['Exclude', 'Auto', 'Include'][internalValue.value] || 'Unknown';
});

const knobColor = computed(() => {
    if (internalValue.value === 0) return '#ef4444'; // red-500
    if (internalValue.value === 1) return '#9ca3af'; // gray-400
    return '#84cc16'; // lime-500
});

const labelColor = computed(() => {
    if (internalValue.value === 0) return '#dc2626'; // red-600
    if (internalValue.value === 1) return '#6b7280'; // gray-500
    return '#65a30d'; // lime-600
});

function handleClick(event: MouseEvent) {
    if (!switchContainer.value) return;
    const rect = switchContainer.value.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;

    if (clickX < width / 3) {
        internalValue.value = 0;
    } else if (clickX < 2 * width / 3) {
        internalValue.value = 1;
    } else {
        internalValue.value = 2;
    }

    emit('change', internalValue.value);
}

function handleTouchStart(event: TouchEvent) {
    const touch = event.touches[0];
    startTouchX = touch.clientX;
    endTouchX = touch.clientX;
}

function handleTouchMove(event: TouchEvent) {
    const touch = event.touches[0];
    endTouchX = touch.clientX;
}

function handleTouchEnd() {
    const swipeDistance = endTouchX - startTouchX;

    if (swipeDistance < -30) {
        internalValue.value = Math.max(0, internalValue.value - 1) as 0 | 1 | 2;
    } else if (swipeDistance > 30) {
        internalValue.value = Math.min(2, internalValue.value + 1) as 0 | 1 | 2;
    }

    emit('change', internalValue.value);
}

watch(() => props.value, (newValue) => {
    internalValue.value = newValue;
});
</script>

<style scoped>
.switch-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 8px 0;
}

.switch {
    -webkit-tap-highlight-color: transparent;
    position: relative;
    width: 100%;
    max-width: 140px;
    height: 10px;
    background-color: #e5e7eb; /* gray-200 */
    border-radius: 10px;
    cursor: pointer;
    box-shadow: inset 0 1px 2px rgba(0,0,0,0.1);
}

.switch-knob {
    position: absolute;
    top: 50%;
    /* CSS-only responsive positioning */
    left: calc((var(--switch-value) / 2) * (100% - 20px));
    margin-top: -10px; /* half of 20px */
    width: 20px;
    height: 20px;
    border-radius: 50%;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    z-index: 10;
}

.knob-inner {
    width: 8px;
    height: 8px;
    background-color: white;
    opacity: 0.5;
    border-radius: 50%;
}

.label-container {
    margin-top: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    min-height: 1rem;
}

.label-container span {
    transition: color 0.3s ease;
}
</style>


